/* src/ui/panels/inventoryGrid.ts — what you own (TASKS.md 3.4).
 *
 * Renders an inventory as a grid of slots, joined against the static catalog for names and
 * categories. Used for both shapes the server emits: the player's own stores (an array) and a
 * cart's stock (an object keyed by commodity id) — the caller normalises, this only draws.
 *
 * Empty is a state worth designing, not an accident: a new player's stores are empty, and a
 * blank rectangle reads as broken. It says so instead.
 */

import type { CatalogItem, InventoryEntry } from '../../core/types.ts';
import { el } from '../dom.ts';
import { formatCount } from '../format.ts';

export interface InventoryGridOptions {
  /** Shown when there is nothing to render. */
  readonly emptyText?: string;
  /** Called when a slot is clicked, if the caller wants selection. */
  readonly onPick?: (itemId: string) => void;
}

function slot(entry: InventoryEntry, item: CatalogItem | undefined): HTMLElement {
  const label = item?.name ?? entry.itemId;
  return el(
    'button',
    {
      class: 'gf-inv-slot',
      attrs: { type: 'button', title: `${label} — ${entry.itemId}` },
      data: { action: 'inv:pick', itemId: entry.itemId },
    },
    el('span', { class: 'gf-inv-name' }, label),
    // Quantities are fractional server-side (0.2 of a mustard per hot dog); a player does not
    // want to read "29.6000", so whole units unless the remainder actually matters.
    el(
      'span',
      { class: 'gf-inv-qty' },
      entry.qty >= 10 ? formatCount(Math.floor(entry.qty)) : entry.qty.toFixed(1),
    ),
  );
}

export function renderInventoryGrid(
  entries: readonly InventoryEntry[],
  catalog: ReadonlyMap<string, CatalogItem>,
  options: InventoryGridOptions = {},
): HTMLElement {
  if (entries.length === 0) {
    return el(
      'p',
      { class: 'gf-inv-empty' },
      options.emptyText ?? 'Nothing here yet. Buy something on the Market tab.',
    );
  }

  const sorted = [...entries].sort((a, b) => {
    const ca = catalog.get(a.itemId)?.category ?? '';
    const cb = catalog.get(b.itemId)?.category ?? '';
    return ca === cb ? a.itemId.localeCompare(b.itemId) : ca.localeCompare(cb);
  });

  return el('div', { class: 'gf-inv-grid' }, ...sorted.map((e) => slot(e, catalog.get(e.itemId))));
}

/** Normalise a cart's keyed inventory (`{FRANKS: 150}`) into the array shape. */
export function inventoryFromRecord(
  record: Readonly<Record<string, number>>,
): readonly InventoryEntry[] {
  return Object.entries(record)
    .filter(([, qty]) => qty > 0)
    .map(([itemId, qty]) => ({ itemId, qty }));
}
